// Imports
// *****************************************************
import { Fragment, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { MainMenuInterface } from '@type/header-types';
import useWindowDimensions from '@hooks/useMediaQuery';
import {
  Anchor,
  LinkStyled,
  Dropdown,
  DropdownButton,
  DropdownItems,
  DropdownItem,
  Button,
} from '@components/common';
import { cn } from '@utils/cn';
import { i18nConfig } from 'i18n-config';
import { clsx } from 'clsx';
import { useDropdownHover } from '@hooks/common/useDropdownHover';
import { isHireRoute } from '@lib/functions/routes/isHireRoute';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { MicrophoneIcon } from '@heroicons/react/20/solid';
import { InterviewSelector } from './InterviewSelector';
import { mainMenuLinks } from './links-objects';
import { MainNavbarLink } from './MainNavbarLink';

/**
 * @description Main menu links component. We use the 'role' to determine which links to show and
 * also to check if the user is authenticated (role === undefined means the user is not authenticated).
 * * **role** - user role ('NONE', 'USER', 'COMPANY') - look at mainMenuLinks object for more info
 * * **currentRoute** - current route pathname - needed to highlight the current route in the menu for React pages
 * * **closePopover** - function to close the dropdown popover
 * * **locale** - the current app locale
 */
export const MainMenuLinks = (props: MainMenuInterface) => {
  // Destructuring props
  const {
    currentRoute = '',
    role,
  } = props;

  // Translate
  const { t } = useTranslation('common');

  // filter the main menu links based on the user login state
  let menusToShow = mainMenuLinks;
  if (role === undefined) {
    menusToShow = mainMenuLinks.filter((link) => link.onlyLoggedIn === false || link.onlyLoggedIn === undefined);
  }
  const { isTablet } = useWindowDimensions();

  const isActiveRoute = (linkRoute: string) => {
    if (!currentRoute) {
      return false;
    }

    // Extract the first segment of the path
    const pathSegments = currentRoute.split('/').filter(Boolean); // filters out empty strings

    // Check if the first segment is a supported locale
    const currentLocale = i18nConfig.locales.includes(pathSegments[0] as Locale) ? pathSegments[0] as Locale : null;

    // Get the base path by removing the current locale if it exists
    const basePath = currentLocale ? currentRoute.replace(`/${currentLocale}`, '') : currentRoute;

    return basePath.startsWith(linkRoute);
  };

  const { dropdownRef, buttonRef, closeFuncRef } = useDropdownHover({ enabled: !isTablet });

  const headerInfo = useAppSelector(getUserHeaderInfo);
  const headerInfoExtended = headerInfo as ApiSchema<'HeaderInfo'> & { discountPercentage?: number };

  // Modal state for CV update
  const [isInterviewSelectorOpen, setIsInterviewSelectorOpen] = useState(false);

  // Render component
  // - Filter the main menu links based on the user role ('NONE', 'USER', 'COMPANY')
  // *****************************************************
  return (
    <>
      { // Main menu links
        menusToShow
          .filter((item) => (isHireRoute(currentRoute) ? item.forSection?.includes('HIRE') : item.forSection?.includes('TALENT')))
          .map((link) => {
            // destructuring link object
            const {
              type,
              label,
              route: linkRoute,
              onlyMobile,
              onlyDesktop,
              icon,
              children,
              dataTestId,
            } = link;

            // If there is selected to show the links only on non desktop devices, return with nothing. We are using isTablet hook.
            if (!isTablet && onlyMobile) { return (<Fragment key={label} />); }
            // If there is selected to show the links only on desktop devices, return with nothing. We are using isTablet hook.
            if (onlyDesktop && isTablet) { return (<Fragment key={label} />); }

            if (children) {
              return (
                <Dropdown key={label} ref={dropdownRef}>
                  {({ open, close }) => {
                    closeFuncRef.current = close;

                    return (
                      <>
                        <DropdownButton
                          as={MainNavbarLink}
                          type={type}
                          route={linkRoute}
                          label={label}
                          icon={icon}
                          className={cn('relative h-full', open && 'bg-surface-50')}
                          ref={buttonRef}
                          {...(dataTestId ? { 'data-test-id': dataTestId } : {})}
                        />
                        <DropdownItems
                          animation="slideUp"
                          position="left"
                          className="fixed bottom-13 left-0 !z-20 flex w-screen min-w-40 items-start gap-1 bg-surface-50 shadow-none lg:absolute lg:bottom-auto lg:top-full lg:w-auto lg:flex-col lg:pb-2 lg:pt-4 lg:shadow-md"
                        >
                          {children.map((item) => {
                            if (!isTablet && item.onlyMobile) { return (<Fragment key={label} />); }

                            const Component = item.type === 'react' ? LinkStyled : Anchor;
                            const isSymfony = item.type === 'symfony';
                            const extraProps = isSymfony ? { symfonyRoute: true } : {};

                            return (
                              <DropdownItem
                                key={item.label}
                                as={Component}
                                href={item.route}
                                styling="none"
                                className={clsx(
                                  'w-full text-nowrap px-4 py-3 text-center text-xs outline-none transition hover:bg-surface sm:text-sm lg:py-2 lg:text-left',
                                  !isSymfony && (isActiveRoute(item.route)) && 'text-secondary',
                                )}
                                {...extraProps}
                                {...(dataTestId ? { 'data-test-id': dataTestId } : {})}
                              >

                                {(item.hasDiscount && headerInfoExtended?.discountPercentage)
                                  ? (
                                    <div className="flex items-center justify-between">
                                      {t(item.label)}
                                      <span
                                        className="ml-1 rounded-md bg-secondary/20 px-1 text-pico font-thin text-secondary"
                                      >
                                        {headerInfoExtended?.discountPercentage}
                                        % OFF
                                      </span>
                                    </div>

                                  ) : t(item.label)}
                              </DropdownItem>
                            );
                          })}
                        </DropdownItems>
                      </>
                    );
                  }}
                </Dropdown>
              );
            }

            if (label === 'header.label.interview') {
              return (
                <Fragment key={label}>
                  {isTablet ? (
                    <div className="relative">
                      <Button
                        styling="none"
                        className={cn(
                          'relative h-full',
                          isTablet
                        && 'absolute bottom-2 left-1/2 -translate-x-1/2 size-14 rounded-full bg-secondary hover:bg-secondary-dark shadow-lg z-40 flex items-center justify-center',
                        )}
                        onClick={() => {
                          setIsInterviewSelectorOpen(true);
                        }}
                      >
                        <MicrophoneIcon className="size-6 lg:hidden" />
                        <span className="hidden lg:block">{t('header.label.interview')}</span>
                      </Button>
                    </div>
                  ) : (
                    <MainNavbarLink
                      key={label}
                      type={type}
                      route={linkRoute}
                      label={label}
                      icon={icon}
                      onClick={(e) => {
                        e.preventDefault();
                        setIsInterviewSelectorOpen(true);
                      }}
                    />
                  )}
                  {/* CV Update Modal */}
                  <InterviewSelector
                    isOpen={isInterviewSelectorOpen}
                    onClose={() => setIsInterviewSelectorOpen(false)}
                  />
                </Fragment>
              );
            }

            return (
              <MainNavbarLink
                key={label}
                type={type}
                route={linkRoute}
                label={label}
                icon={icon}
                {...(dataTestId ? { 'data-test-id': dataTestId } : {})}
              />
            );
          })
      }

    </>
  );
};
